import VerificationBadge from '../../../components/verification-badge/VerificationBadge';
import TrackEventLink from '../../../components/track-event-link/TrackEventLink';
import { useProject } from '../../project-hooks/useProject';
import { isOwnerAndReceiverSame, isProjectADugnad } from '../../project-model';
import { ProfileImage } from './ProfileImage';
import Chevron from '../../../components/chevron/Chevron';
import { useLayoutEffect, useRef, useState } from 'react';
import { Heading4 } from '../../../components/typography/Heading';
import { formatOrganizationNumber } from '../../../utils';
import ButtonText from '../../../components/button/button-text/ButtonText';
import styles from './ProjectOwnerAndReceiver.module.less';
import { useIsClientMounted } from '../../../components/hooks/isClientMounted';
import I18nText from '../../i18n/I18nText';
import {
    CLOSE_TEXT_KEY,
    OWNS_PROJECT_TEXT_KEY,
    READ_MORE_TEXT_KEY,
    RECEIVES_MONEY_TEXT_KEY,
} from '../../i18n/project-i18n-model';

const ProjectUserLink = ({ projectUser, className = '' }) => {
    const classNames = [projectUser.type === 'org' ? styles.organizationName : null, styles.name, className].join(' ');
    return (
        <>
            {projectUser.showVerifiedBadge ? (
                <VerificationBadge verified={projectUser.isVerified} className={styles.badge} />
            ) : null}
            {projectUser.eventId && projectUser.eventIsReceiverType ? (
                <div className={classNames}>
                    <TrackEventLink to={`/events/${projectUser.eventId}`}>{projectUser?.eventName}</TrackEventLink>
                </div>
            ) : projectUser.profileUrl ? (
                <TrackEventLink to={projectUser.profileUrl} className={classNames}>
                    {projectUser?.name}
                </TrackEventLink>
            ) : (
                <span className={classNames}>{projectUser.name}</span>
            )}
        </>
    );
};

const ReceiverInfo = ({ receiverInformation, showOrganizationDescription, setShowOrganizationDescription }) => {
    if (receiverInformation?.isInvited) {
        return <div className={styles.invitedInfo}>En annen person er blitt invitert som mottaker</div>;
    } else if (receiverInformation?.name) {
        return (
            <>
                <ProfileImage projectUser={receiverInformation} />
                <div>
                    <ProjectUserLink projectUser={receiverInformation} />{' '}
                    <span className={styles.info}>
                        <I18nText textKey={RECEIVES_MONEY_TEXT_KEY} />
                    </span>
                </div>
                {receiverInformation.description && (
                    <div
                        onClick={() => {
                            setShowOrganizationDescription(!showOrganizationDescription);
                        }}
                        className={styles.chevronContainer}
                    >
                        <Chevron className={styles.chevron} isOpen={showOrganizationDescription} />
                    </div>
                )}
            </>
        );
    } else {
        return <div>Mottaker er ikke valgt ennå</div>;
    }
};

const ProjectOwnerAndReceiver = () => {
    const project = useProject();
    const isClientMounted = useIsClientMounted();

    const { ownerInformation, receiverInformation } = project;
    const isSame = isOwnerAndReceiverSame(ownerInformation, receiverInformation);
    const [showOrganizationDescription, setShowOrganizationDescription] = useState(false);

    const ownerInformationIsOverridden = !!ownerInformation?.override;
    const hideResponsibleUser = !!project.profile?.hideResponsibleUser;
    const owner = ownerInformation?.override ?? ownerInformation;

    return (
        <div className={styles.ownerAndReceiver}>
            {!isSame && owner && (
                <div className={styles.profile}>
                    <ProfileImage projectUser={owner} />
                    <div>
                        <ProjectUserLink projectUser={owner} />{' '}
                        <div className={styles.info}>
                            {ownerInformationIsOverridden && !hideResponsibleUser ? (
                                <ProjectUserLink projectUser={ownerInformation} className={styles.ownerName} />
                            ) : null}{' '}
                            {isProjectADugnad(project) ? (
                                'står bak dugnaden'
                            ) : (
                                <I18nText textKey={OWNS_PROJECT_TEXT_KEY} />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.profile}>
                <ReceiverInfo
                    receiverInformation={receiverInformation}
                    showOrganizationDescription={showOrganizationDescription}
                    setShowOrganizationDescription={setShowOrganizationDescription}
                />
            </div>
            {receiverInformation?.description && isClientMounted && (
                <ReceiverDescription
                    receiverInformation={receiverInformation}
                    showOrganizationDescription={showOrganizationDescription}
                />
            )}
        </div>
    );
};

const ReceiverDescription = ({ receiverInformation, showOrganizationDescription }) => {
    const [organizationDescriptionIsLimited, setOrganizationDescriptionIsLimited] = useState(true);
    const [showAllOrganizationDescription, setShowAllOrganizationDescription] = useState(false);
    const organizationDescriptionRef = useRef();

    useLayoutEffect(() => {
        setOrganizationDescriptionIsLimited(
            (organizationDescriptionRef.current?.clientHeight || 0) <
                (organizationDescriptionRef.current?.scrollHeight || 0)
        );
    }, [
        organizationDescriptionRef.current?.clientHeight,
        organizationDescriptionRef.current?.scrollHeight,
        showOrganizationDescription,
    ]);

    return (
        <div
            className={[
                styles.organizationDescription,
                showOrganizationDescription ? styles.showOrganizationDescription : styles.hideOrganizationDescription,
            ].join(' ')}
        >
            <Heading4>Om organisasjonen</Heading4>
            <div className={showAllOrganizationDescription ? '' : styles.limitedOrganizationDescription}>
                <div
                    dangerouslySetInnerHTML={{ __html: receiverInformation.description }}
                    ref={organizationDescriptionRef}
                />
            </div>
            <div className={styles.organizationNumber}>
                Organisasjonsnummer: {formatOrganizationNumber(receiverInformation.organizationNumber)}
            </div>
            {organizationDescriptionIsLimited && (
                <div className={styles.readMore}>
                    {showAllOrganizationDescription ? (
                        <ButtonText
                            className={styles.closeTextButton}
                            onClick={() => setShowAllOrganizationDescription(false)}
                        >
                            <I18nText textKey={CLOSE_TEXT_KEY} />
                        </ButtonText>
                    ) : (
                        <ButtonText onClick={() => setShowAllOrganizationDescription(true)}>
                            <I18nText textKey={READ_MORE_TEXT_KEY} /> <Chevron />
                        </ButtonText>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProjectOwnerAndReceiver;
